// The marketplace headers the olap/analytics interceptors stamp from the global picker, and the
// per-request overrides that opt out of them. Pure, so a spec can drive each case.

const hasHeader = (headers, name) =>
  typeof headers?.has === 'function' ? headers.has(name) : headers?.[name] != null;

// A request that names its own scope keeps it whole: an explicit X-Profile-Id (e.g. the marketplace
// KPI fan-out, one profile per request) or an explicit X-Marketplace-Id (the Search Term Results
// grid, which queries a marketplace the client has no Ads profile or seller in). The picker's ids
// are never mixed with an overridden one. axios 1.x headers are a case-insensitive AxiosHeaders
// object, so a plain bracket check misses the override: use .has() when available.
export const hasScopeOverride = (headers) =>
  hasHeader(headers, 'X-Profile-Id') || hasHeader(headers, 'X-Marketplace-Id');

// The Selling Partner id(s) of the SP accounts that cover a marketplace (canonical marketplace
// string id <-> SP marketplaceId, the same join as spLinkStatus.js). Several covering accounts are
// comma-joined; null when none does.
export function sellingPartnerIdsFor(spAccounts, marketplaceStringId) {
  if (!marketplaceStringId) return null;
  const ids = (spAccounts || [])
    .filter((a) => (a.marketplaces || []).some((m) => m.marketplaceId === marketplaceStringId))
    .map((a) => a.sellingPartnerId)
    .filter(Boolean);
  return ids.length ? [...new Set(ids)].join(',') : null;
}

// Stamps the picked marketplace's identifiers; the marketplace may carry any subset: ads
// marketplaces have profileId (+ marketplaceStringId); seller-only ones (a Selling Partner
// connection, no ads profile) have sellingPartnerId + marketplaceStringId but no profileId.
// Everything we have is sent and the backend resolves the group. No-op on an overridden request.
export function stampMarketplaceScope(headers, marketplace, spAccounts) {
  if (hasScopeOverride(headers)) return;
  if (marketplace?.profileId) headers['X-Profile-Id'] = String(marketplace.profileId);
  if (marketplace?.marketplaceStringId) {
    headers['X-Marketplace-Id'] = String(marketplace.marketplaceStringId);
  }
  // Seller-only marketplaces carry the SP id directly; ads marketplaces don't, so resolve the
  // current BP's SP account(s) covering this marketplace. Sent alongside X-Profile-Id.
  const sellingPartnerId =
    marketplace?.sellingPartnerId
    || sellingPartnerIdsFor(spAccounts, marketplace?.marketplaceStringId);
  if (sellingPartnerId) headers['X-Selling-Partner-Id'] = String(sellingPartnerId);
}
